import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";

import type { AcademicYear } from "../../services/academicSetupService";
import { scholarshipService, type EligibleStudent, type Scholarship, type ScholarshipApplication, type ScholarshipAward } from "../../services/scholarshipService";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { COVERAGE, PAYMENT_METHODS, TYPES, payable, ugx } from "./scholarshipLogic";

type Common = { busy: boolean; error?: string; onClose: () => void };
const text = (value?: string | null) => value ?? "";

/** What the school offers: its kind, value, who can apply and by when. */
export function ScholarshipForm({ record, years, busy, error, onSave, onClose }: Common & { record?: Scholarship; years: AcademicYear[]; onSave: (data: Partial<Scholarship>) => void }) {
  const current = years.find((item) => item.is_current);
  const [name, setName] = useState(text(record?.name));
  const [type, setType] = useState(record?.scholarship_type ?? "academic");
  const [coverage, setCoverage] = useState(record?.coverage ?? "tuition");
  const [amount, setAmount] = useState(record ? String(Number(record.amount)) : "");
  const [sponsor, setSponsor] = useState(text(record?.sponsor));
  const [criteria, setCriteria] = useState(text(record?.eligibility_criteria));
  const [description, setDescription] = useState(text(record?.description));
  const [year, setYear] = useState(String(record?.academic_year ?? current?.id ?? ""));
  const chosenYear = years.find((item) => String(item.id) === year);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const optional = (key: string) => String(form.get(key) ?? "").trim() || null;
    onSave({
      name: name.trim(),
      scholarship_type: type,
      coverage,
      amount,
      currency: "UGX",
      academic_year: Number(year),
      application_deadline: String(form.get("deadline")),
      sponsor: sponsor.trim() || null,
      eligibility_criteria: criteria.trim(),
      description: description.trim() || null,
      minimum_gpa: optional("minimum_gpa"),
      income_limit: optional("income_limit"),
      requirements: optional("requirements"),
      contact_person: optional("contact_person"),
      contact_phone: optional("contact_phone"),
      contact_email: optional("contact_email"),
      ...(record ? {} : { is_active: true, is_available: true }),
    });
  };
  const aiParams = { name, scholarship_type: type, coverage, amount: Number(amount) || null, sponsor, eligibility_criteria: criteria };
  return (
    <FormModal id="scholarship-form" size="3xl" title={record ? `Edit ${record.name}` : "New scholarship"} busy={busy} error={error} saveLabel={record ? "Save changes" : "Add scholarship"} note="Families see open scholarships until the deadline." onClose={onClose} onSubmit={submit}>
      <FormSection title="Scholarship" divided={false} columns={2}>
        <Field label="Name" className="sm:col-span-2">
          <input required autoFocus maxLength={200} value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Head Teacher's Excellence Award" />
        </Field>
        <Field label="Kind">
          <select value={type} onChange={(event) => setType(event.target.value)} className="input mt-1">{TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </Field>
        <Field label="Covers">
          <select value={coverage} onChange={(event) => setCoverage(event.target.value)} className="input mt-1">{COVERAGE.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </Field>
        <Field label="Worth per learner (UGX)" hint="The most one award can be.">
          <input required type="number" min={1} step="1" value={amount} onChange={(event) => setAmount(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Sponsor" optional>
          <input maxLength={200} value={sponsor} onChange={(event) => setSponsor(event.target.value)} className="input mt-1" placeholder="Who funds it" />
        </Field>
      </FormSection>

      <FormSection title="Applications" columns={2}>
        <Field label="Academic year">
          <select required value={year} onChange={(event) => setYear(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a year</option>
            {years.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Apply by" hint={chosenYear ? `Within ${chosenYear.name}: ${chosenYear.start_date} to ${chosenYear.end_date}.` : undefined}>
          <input required type="date" name="deadline" defaultValue={record?.application_deadline ?? ""} min={record ? undefined : todayIso()} max={chosenYear?.end_date} className="input mt-1" />
        </Field>
        <Field as="div" label="Who can apply" className="sm:col-span-2" hint="Families check themselves against this before applying.">
          <AIAssistField rows={3} value={criteria} onChange={(next) => setCriteria(next)} placeholder="e.g. Learners in P5–P7 with an average of 75% or more" context={{ key: "scholarship.note", params: { kind: "eligibility", ...aiParams } }} draftLabel="Draft criteria" draftHint="Jot the requirements and it lists them plainly. It never adds one." />
        </Field>
        <Field label="Minimum GPA" optional hint="Only if the school grades in GPA.">
          <input type="number" name="minimum_gpa" min={0} max={5} step="0.01" defaultValue={record?.minimum_gpa ?? ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Household income limit (UGX)" optional>
          <input type="number" name="income_limit" min={1} step="1" defaultValue={record?.income_limit ? Number(record.income_limit) : ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Documents to attach" optional className="sm:col-span-2">
          <textarea name="requirements" rows={2} defaultValue={text(record?.requirements)} className="input mt-1" placeholder="e.g. Last term's report card; a letter from the LC1 chairperson" />
        </Field>
      </FormSection>

      <FormSection title="Description" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField rows={3} value={description} onChange={(next) => setDescription(next)} placeholder="What the scholarship is for, in a sentence or two" context={{ key: "scholarship.note", params: { kind: "description", ...aiParams } }} draftLabel="Draft description" draftHint={name.trim() ? "Uses the name, kind, cover, value and sponsor." : "Name it first, then draft."} />
        </Field>
      </FormSection>

      <FormSection title="Contact" description="Who families ask about it." columns={3}>
        <Field label="Person" optional><input name="contact_person" maxLength={100} defaultValue={text(record?.contact_person)} className="input mt-1" /></Field>
        <Field label="Phone" optional><input name="contact_phone" maxLength={20} defaultValue={text(record?.contact_phone)} className="input mt-1" /></Field>
        <Field label="Email" optional><input type="email" name="contact_email" defaultValue={text(record?.contact_email)} className="input mt-1" /></Field>
      </FormSection>
    </FormModal>
  );
}

/** A family's application: the learner, the scholarship, and their case in their own words. */
export function ApplyForm({ scholarships, learners, scholarshipId, busy, error, onSave, onClose, isParent }: Common & {
  scholarships: Scholarship[];
  learners: EligibleStudent[];
  scholarshipId?: number;
  isParent: boolean;
  onSave: (data: Partial<ScholarshipApplication>) => void;
}) {
  const [scholarship, setScholarship] = useState(String(scholarshipId ?? (scholarships.length === 1 ? scholarships[0].id : "")));
  const [student, setStudent] = useState(learners.length === 1 ? String(learners[0].id) : "");
  const [statement, setStatement] = useState("");
  const [need, setNeed] = useState("");
  const chosen = scholarships.find((item) => String(item.id) === scholarship);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSave({
      student: Number(student),
      scholarship: Number(scholarship),
      personal_statement: statement.trim(),
      financial_need: need.trim(),
      academic_achievements: String(form.get("achievements") ?? "").trim(),
      extracurricular_activities: String(form.get("activities") ?? "").trim(),
    });
  };
  const params = { scholarship: Number(scholarship) || null, student: Number(student) || null };
  return (
    <FormModal id="apply-form" size="3xl" title="Apply for a scholarship" busy={busy} error={error} saveLabel="Submit application" note={chosen ? `Apply by ${chosen.application_deadline}.` : undefined} onClose={onClose} onSubmit={submit}>
      <FormSection title="Who and what" divided={false} columns={2}>
        <Field as="div" label={isParent ? "Your child" : "Learner"}>
          {isParent && learners.length <= 6 ? (
            <select required value={student} onChange={(event) => setStudent(event.target.value)} className="input mt-1">
              <option value="" disabled>Choose</option>
              {learners.map((item) => <option key={item.id} value={item.id}>{item.full_name} ({item.student_id})</option>)}
            </select>
          ) : (
            <SearchPick items={learners} value={student} onChange={setStudent} label={(item) => item.full_name} detail={(item) => `${item.student_id}${item.class_name ? ` · ${item.class_name}` : ""}`} placeholder="Search by name or number" empty="No learner matches" required />
          )}
        </Field>
        <Field label="Scholarship" hint={chosen ? `${ugx(chosen.amount)} · ${chosen.eligibility_criteria}` : "Only scholarships open to applications are listed."}>
          <select required value={scholarship} onChange={(event) => setScholarship(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a scholarship</option>
            {scholarships.map((item) => <option key={item.id} value={item.id}>{item.name} · apply by {item.application_deadline}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="The case" description="In your own words. The assistant only tidies what you write; it never adds to it." columns={1}>
        <Field as="div" label="Personal statement">
          <AIAssistField rows={4} value={statement} onChange={(next) => setStatement(next)} placeholder="Why the learner is applying and what the scholarship would mean" context={{ key: "scholarship.note", params: { kind: "statement", ...params } }} draftLabel="Tidy my notes" draftHint="Write your points first, then let it put them into sentences." />
        </Field>
        <Field as="div" label="Financial need" optional>
          <AIAssistField rows={3} value={need} onChange={(next) => setNeed(next)} placeholder="The family's circumstances, if the scholarship considers need" context={{ key: "scholarship.note", params: { kind: "need", ...params } }} draftLabel="Tidy my notes" draftHint="Write your points first. It never adds a circumstance." />
        </Field>
        <Field label="Achievements" optional>
          <textarea name="achievements" rows={2} className="input mt-1" placeholder="e.g. Top of class in Term 2; district spelling bee finalist" />
        </Field>
        <Field label="Clubs, sport and activities" optional>
          <textarea name="activities" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** An award for an approved application: how much, from when, until when. */
export function AwardForm({ application, busy, error, onSave, onClose }: Common & { application: ScholarshipApplication; onSave: (data: Partial<ScholarshipAward>) => void }) {
  const max = Number(application.scholarship_amount ?? 0);
  const [amount, setAmount] = useState(max ? String(max) : "");
  const [awarded, setAwarded] = useState(todayIso());
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSave({
      application: application.id,
      award_amount: amount,
      award_date: awarded,
      effective_date: String(form.get("effective")),
      expiry_date: String(form.get("expiry") ?? "") || null,
      renewal_requirements: String(form.get("renewal") ?? "").trim() || null,
      terms_conditions: String(form.get("terms") ?? "").trim() || null,
    });
  };
  return (
    <FormModal id="award-form" title={`Award ${application.scholarship_name}`} busy={busy} error={error} saveLabel="Make the award" note={`To ${application.student_name}.`} onClose={onClose} onSubmit={submit}>
      <FormSection title="The award" divided={false} columns={2}>
        <Field label="Amount (UGX)" hint={max ? `The scholarship is worth ${ugx(max)}; a partial award is fine.` : undefined} className="sm:col-span-2">
          <input required type="number" min={1} max={max || undefined} step="1" value={amount} onChange={(event) => setAmount(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Awarded on"><input required type="date" value={awarded} onChange={(event) => setAwarded(event.target.value)} className="input mt-1" /></Field>
        <Field label="Takes effect from"><input required type="date" name="effective" min={awarded} defaultValue={awarded} className="input mt-1" /></Field>
        <Field label="Runs until" optional hint="Leave blank for an award without an end date."><input type="date" name="expiry" min={awarded} className="input mt-1" /></Field>
      </FormSection>
      <FormSection title="Terms" columns={1}>
        <Field label="To keep it, the learner must" optional><textarea name="renewal" rows={2} className="input mt-1" placeholder="e.g. Keep an average of 70% and attendance of 90%" /></Field>
        <Field label="Other terms" optional><textarea name="terms" rows={2} className="input mt-1" /></Field>
      </FormSection>
    </FormModal>
  );
}

/** Money paid out of an award. */
export function PaymentForm({ award, busy, error, onSave, onClose }: Common & { award: ScholarshipAward; onSave: (data: Record<string, unknown>) => void }) {
  const left = payable(award).amount;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSave({
      award: award.id,
      amount: String(form.get("amount")),
      payment_date: String(form.get("date")),
      payment_method: String(form.get("method")),
      reference_number: String(form.get("reference")).trim(),
      notes: String(form.get("notes") ?? "").trim() || null,
    });
  };
  return (
    <FormModal id="payment-form" size="xl" title="Record a disbursement" busy={busy} error={error} saveLabel="Record disbursement" note={`${award.application_name} · ${award.scholarship_name}`} onClose={onClose} onSubmit={submit}>
      <FormSection title="Payment" divided={false} columns={2}>
        <Field label="Amount (UGX)" hint={`Up to ${ugx(left)} is left on the award.`}>
          <input required autoFocus type="number" name="amount" min={1} max={left} step="1" defaultValue={left || ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Paid on"><input required type="date" name="date" defaultValue={todayIso()} min={award.effective_date} max={award.expiry_date ?? undefined} className="input mt-1" /></Field>
        <Field label="How"><select name="method" className="input mt-1">{PAYMENT_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
        <Field label="Reference" hint="Bank or receipt reference."><input required name="reference" maxLength={100} className="input mt-1" /></Field>
        <Field label="Notes" optional className="sm:col-span-2"><textarea name="notes" rows={2} className="input mt-1" /></Field>
      </FormSection>
    </FormModal>
  );
}

/** A renewal review, starting from the learner's year in the records. */
export function RenewalForm({ award, busy, error, onSave, onClose }: Common & { award: ScholarshipAward; onSave: (data: Record<string, unknown>) => void }) {
  const measures = useQuery({ queryKey: ["scholarship", "measures", award.id], queryFn: () => scholarshipService.getAwardMeasures(award.id) });
  const [attendance, setAttendance] = useState("");
  useEffect(() => {
    if (measures.data?.attendance_days) setAttendance(String(Number(measures.data.attendance_percentage)));
  }, [measures.data]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSave({
      award: award.id,
      renewal_period: String(form.get("period")).trim(),
      renewal_date: String(form.get("date")),
      new_expiry_date: String(form.get("expiry")),
      current_gpa: String(form.get("gpa") ?? "") || null,
      attendance_rate: attendance || null,
      notes: String(form.get("notes") ?? "").trim() || null,
    });
  };
  const m = measures.data;
  return (
    <FormModal id="renewal-form" title="Review for renewal" busy={busy} error={error} saveLabel="Send for decision" note={`${award.application_name} · ${award.scholarship_name}`} onClose={onClose} onSubmit={submit}>
      {award.renewal_requirements && <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"><span className="font-medium">To keep it: </span>{award.renewal_requirements}</p>}
      <FormSection title="Renewal" divided={false} columns={2}>
        <Field label="For" hint="e.g. 2027, or Term 1 2027"><input required autoFocus name="period" maxLength={100} className="input mt-1" /></Field>
        <Field label="Reviewed on"><input required type="date" name="date" defaultValue={todayIso()} className="input mt-1" /></Field>
        <Field label="Would run until" hint={award.expiry_date ? `Now ends ${award.expiry_date}.` : undefined}><input required type="date" name="expiry" min={award.expiry_date ?? undefined} className="input mt-1" /></Field>
      </FormSection>
      <FormSection title="The learner's year" description={m ? `From the records for ${m.academic_year_name}: ${m.attendance_days} register days, ${m.results_counted} published results${m.results_counted ? ` averaging ${Number(m.average_grade)}%` : ""}.` : measures.isLoading ? "Reading the registers and results…" : "The records could not be read."} columns={2}>
        <Field label="Attendance (%)" optional hint={m && !m.attendance_days ? "No registers yet; enter it only if you have it." : undefined}>
          <input type="number" min={0} max={100} step="0.01" value={attendance} onChange={(event) => setAttendance(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="GPA" optional hint="Only if the school grades in GPA."><input type="number" name="gpa" min={0} max={5} step="0.01" className="input mt-1 tabular-nums" /></Field>
        <Field label="Notes" optional className="sm:col-span-2"><textarea name="notes" rows={2} className="input mt-1" /></Field>
      </FormSection>
    </FormModal>
  );
}
